import { isNil, get, forEach } from 'lodash';

import { STATUS_TYPES } from './taskConstants';
import * as nodeOps from './task-node-operations';

/**
 * Gets the task id.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The id for the task or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getId = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, '_id', '');
};

/**
 * Gets the task name.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The name for the task or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getName = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'name', '');
};

/**
 * Gets the task label.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The label for the task or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getLabel = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'label', '');
};

/**
 * Gets the task group.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The group for the task or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getGroup = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'group', '');
};

/**
 * Gets the task nodes.
 *
 * @param {object} taskObject - The task object.
 * @returns {Array} The nodes for the task or empty array if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getNodes = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'nodes', []);
};

/**
 * Gets the task links.
 *
 * @param {object} taskObject - The task object.
 * @returns {Array} The links for the task or empty array if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getLinks = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'links', []);
};

/**
 * Gets the task ready status.
 *
 * @param {object} taskObject - The task object.
 * @returns {boolean} True if all nodes are ready, false otherwise.
 * @throws {Error} If task is null or undefined.
 */
export const isTaskReady = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  let areAllNodesReady = true;

  forEach(getNodes(taskObject), node => {
    if (!nodeOps.isNodeReady(node)) {
      areAllNodesReady = false;
      return false;
    }
    return true;
  });

  return areAllNodesReady;
};

/**
 * Check if the task is ready to use.
 *
 * @param {object} taskObject - The task object.
 * @returns {boolean} True if the task is ready to use, false otherwise.
 * @throws {Error} If task is null or undefined.
 */
export const isReady = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'ready', false);
};

/**
 * Get the date when the task started running.
 *
 * @param {object} taskObject - The task object.
 * @returns {boolean} The date when the task started or an empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getStartDate = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'startedAt', '');
};

/**
 * Get the date when the process finished.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The date when the task finished or an empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getFinishDate = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'finishedAt', '');
};

/**
 * Gets the task progress.
 *
 * @param {object} taskObject - The node object.
 * @returns {number} The progress for the task or 0 if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getProgress = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'progress', 0);
};

/**
 * Gets the task status.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The status for the task or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getStatus = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'status', '');
};

/**
 * Checks if task status is paused.
 *
 * @param {object} taskObject - The task object.
 * @returns {boolean} True if task status is paused, false otherwise.
 * @throws {Error} If task is null or undefined.
 */
export const isPaused = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'status', '') === STATUS_TYPES.PAUSED;
};

/**
 * Checks if task status is canceled.
 *
 * @param {object} taskObject - The task object.
 * @returns {boolean} True if task status is canceled, false otherwise.
 * @throws {Error} If task is null or undefined.
 */
export const isCanceled = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'status', '') === STATUS_TYPES.CANCELED;
};

/**
 * Gets the task comments.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The comments for the task or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getComments = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'comments', '');
};

/**
 * Gets the task id in the geoserver.
 *
 * @param {object} taskObject - The task object.
 * @returns {string} The task id in the geoserver or empty string if not found.
 * @throws {Error} If task is null or undefined.
 */
export const getProcessId = taskObject => {
  if (isNil(taskObject)) {
    throw new Error('task not defined');
  }
  return get(taskObject, 'processId', '');
};
